/* eslint-disable @typescript-eslint/no-explicit-any */
import { useLocation, useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import moment from "moment";
import { Button, Chip, useMediaQuery } from "@mui/material";
import styles from "../../Styles/dashboard.module.css";
import { OrderContext } from "../../Contexts/OrderContext";
import { Vocabulary } from "../../Utils/Vocabulary";
import theme from "../../Themes/Theme";
import { dayPilotFormat, euFormatForDateTime } from "../../Utils/Config";
import { FiltersModel, LineModel, OrderModel, Step } from "../../Utils/Models";
import { localUrlEnum, urlEnum } from "../../Utils/UrlEnum";
import { postData } from "../../Services/postData";
import { HttpStatusCode } from "../../Utils/Constants";
import { getData } from "../../Services/getData";
import Filters from "../../Components/Reports/Filters";
import MainTable from "../../Components/GenericComponents/MainTable";
import GenericModal from "../../Components/GenericComponents/GenericModal";
import OrderWorkflow from "../../Components/Orders/OrderWorkflows/OrderWorkflow";
import TimeTracking from "../../Components/Orders/TimeTracking/TimeTracking";
import CheckInModal from "../../Components/CheckIn/CheckInModal";
import { ordersType } from "../../Utils/AutocompleteUtils";
import { styleForTableWithTabs } from "../Users";
import { deleteData } from "../../Services/deleteData";
import { priorityArray } from "../../Components/Orders/OrderHeader";
import { getTextColorBasedOnBg } from "../../Utils/Utils";

export default function Orders() {
  const navigate = useNavigate();
  const location = useLocation();
  const orderContext = useContext(OrderContext);
  const phoneStyle = useMediaQuery("(max-width: 800px)");
  const [openTimeTracking, setOpenTimeTracking] = useState(false);
  const [openEditStep, setOpenEditStep] = useState(false);
  const [openCheckIn, setOpenCheckIn] = useState(false);
  const [modifyStep, setModifyStep] = useState<any>({
    step: null,
    index: 0,
  });

  const [priorityColors, setPriorityColors] = useState<Record<string, string>>(
    {},
  );
  const [filters, setFilters] = useState<FiltersModel>({
    startDate: moment().startOf("year"),
    endDate: moment().endOf("month"),
    user: [],
    customer: [],
    orderStatus: null,
    device: [],
    workflow: null,
    workflowStatus: null,
    product: null,
  });

  useEffect(() => {
    getData(urlEnum.priorityColors).then((res: any) => {
      if (res?.data?.result) {
        const colorsMap: Record<string, string> = {};
        res.data.result.forEach((pc: any) => {
          colorsMap[pc.priority] = pc.color;
        });
        setPriorityColors(colorsMap);
      }
    });
  }, []);

  /**
   *
   */
  const ordersHeaderMobile = [
    {
      label: Vocabulary.orderReference,
      name: "orderNumber",
      options: {
        sort: true,
      },
    },
    {
      label: Vocabulary.timeTracking,
      name: "_id",
      options: {
        filter: false,
        setCellHeaderProps: () => ({
          align: "center",
        }),
        setCellProps: () => ({
          align: "center",
        }),
        sort: false,
        empty: true,
        customBodyRender: (value: any) => {
          return (
            <AccessTimeIcon
              style={{
                color: theme.palette.primary?.main,
                cursor: "pointer",
                padding: 5,
                borderRadius: 4,
                border: `1.7px solid ${theme.palette.primary?.main}`,
              }}
              onClick={(e: any) => {
                e.stopPropagation();
                e.preventDefault();
                getOrder(value);
                setOpenTimeTracking(true);
              }}
            />
          );
        },
      },
    },
  ];
  /**
   *
   */
  const ordersHeader = [
    {
      label: Vocabulary.orderReference,
      name: "orderNumber",
      options: {
        sort: true,
      },
    },
    {
      label: Vocabulary.priority,
      name: "priority",
      options: {
        sort: true,
        customBodyRender: (value: any) => {
          const color = priorityColors[value];
          const label =
            priorityArray.find((p) => p.name === value)?.label || value;
          if (color) {
            return (
              <Chip
                label={label}
                style={{
                  backgroundColor: color,
                  color: getTextColorBasedOnBg(color),
                }}
              />
            );
          }
          return <>{label}</>;
        },
      },
    },
    {
      label: Vocabulary.device,
      name: "device",
      options: {
        sort: false,
        customBodyRender: (value: any) => {
          return <>{value?.name || ""}</>;
        },
      },
    },
    {
      label: Vocabulary.workflow,
      name: "workflow",
      options: {
        sort: false,
        customBodyRender: (value: any) => {
          return <>{value?.name || ""}</>;
        },
      },
    },
    {
      label: Vocabulary.products,
      name: "lines",
      options: {
        sort: false,
        customBodyRender: (values: any) => {
          const validProducts =
            values && values.filter((value: any) => value.product);
          return (
            <>
              {validProducts &&
                validProducts.slice(0, 3).map((value: any, index: number) => {
                  return (
                    <Chip
                      key={value.product.code}
                      label={`${value.product.code} - ${
                        value.product.name.length > 7
                          ? `${value.product.name.substring(0, 7)}...`
                          : value.product.name
                      }`}
                      style={{
                        margin: 5,
                        backgroundColor: "#404a694d",
                      }}
                    />
                  );
                })}

              {validProducts && validProducts.length > 3 && (
                <Chip
                  label="..."
                  style={{
                    margin: 5,
                    backgroundColor: "#404a694d",
                  }}
                />
              )}
            </>
          );
        },
      },
    },
    {
      label: Vocabulary.workflowStatus,
      name: "status",
      options: {
        sort: true,
        display: "false",
        customBodyRender: (value: any) => {
          return <>{value?.name || ""}</>;
        },
      },
    },
    {
      label: Vocabulary.user,
      name: "handledBy",
      options: {
        sort: false,
        display: "false",
        customBodyRender: (value: any) => {
          return <>{value ? `${value.name}` : ""}</>;
        },
      },
    },
    {
      label: Vocabulary.customer,
      name: "customer",
      options: {
        sort: false,
        display: "false",
        customBodyRender: (value: any) => {
          return <>{value ? `${value.name}` : ""}</>;
        },
      },
    },
    {
      label: Vocabulary.orderStatus,
      name: "isClosed",
      options: {
        sort: true,
        customBodyRender: (value: any) => {
          return <>{value ? Vocabulary.closed : Vocabulary.open}</>;
        },
      },
    },
    {
      label: Vocabulary.date,
      name: "createdAt",
      options: {
        sort: true,
        customBodyRender: (value: any) => {
          return <>{moment(value).format(euFormatForDateTime)}</>;
        },
      },
    },

    {
      label: Vocabulary.timeTracking,
      name: "_id",
      options: {
        filter: false,
        setCellHeaderProps: () => ({
          align: "center",
        }),
        setCellProps: () => ({
          align: "center",
        }),
        sort: false,
        empty: true,
        customBodyRender: (value: any) => {
          return (
            <AccessTimeIcon
              style={{
                color: theme.palette.primary?.main,
                cursor: "pointer",
                padding: 5,
                borderRadius: 4,
                border: `1.7px solid ${theme.palette.primary?.main}`,
              }}
              onClick={(e: any) => {
                e.stopPropagation();
                e.preventDefault();
                getOrder(value);
                setOpenTimeTracking(true);
              }}
            />
          );
        },
      },
    },
  ];

  /**
   *
   */
  useEffect(() => {
    let search: any = decodeURIComponent(location.search);
    search = search.split(/[?&]+/);
    const newFilters: any = {
      startDate: moment().startOf("year"),
      endDate: moment().endOf("month"),
      user: [],
      customer: [],
      orderStatus: null,
      device: [],
      workflow: null,
      workflowStatus: null,
      product: null,
    };
    search.forEach((value: any) => {
      if (value !== "") {
        const values = value.split("=");
        newFilters[values[0]] = isNaN(+values[1]) ? values[1] : +values[1];
        if (values[0] === "page") newFilters[values[0]] = +values[1] - 1;
        if (values[0] === "user") newFilters[values[0]] = values[1].split(",");
        if (values[0] === "customer")
          newFilters[values[0]] = values[1].split(",");
        if (values[0] === "device")
          newFilters[values[0]] = values[1].split(",");
        if (values[1] === "null") newFilters[values[0]] = null;
      }
    });
    setFilters({
      ...newFilters,
      startDate: moment(newFilters.startDate, dayPilotFormat),
      endDate: moment(newFilters.endDate, dayPilotFormat),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  /**
   *
   * @param id
   */
  function getOrder(id: string) {
    getData(`${urlEnum.orders}/${id}`)
      .then((res) => {
        if (res) {
          const resOrder = res.data.result;
          if (resOrder.lines && resOrder.lines.length === 0)
            resOrder.lines = [new LineModel()];
          if (resOrder.notes && resOrder.notes.length !== 0)
            resOrder.notes = resOrder.notes.reverse();
          orderContext.setOrder(res.data.result);
        }
      })
      .catch((err) => {
        //
      });
  }
  /**
   *
   * @param step
   * @param index
   */
  function handleChangeModifyStep(step: Step | null, index: number) {
    setModifyStep({ step: step, index: index });
  }

  /**
   *
   * @param item
   */
  function handleEdit(item: OrderModel | null) {
    navigate(`${localUrlEnum.orders}/${item?._id ? item?._id : "newOrder"}`);
  }

  /**
   * Add or update step to order
   */
  function addOrUpdateStepToOrder(createdBy?: any) {
    setOpenEditStep(false);
    const formData: any = new FormData();
    formData.append("stepId", modifyStep.step.stepId._id);
    formData.append(
      "description",
      modifyStep.step.description &&
        modifyStep.step.description !== "" &&
        modifyStep.step.description !== "null" &&
        modifyStep.step.description !== "undefined"
        ? modifyStep.step.description
        : null,
    );
    if (modifyStep.step.customStepValue) {
      formData.append(
        "customStepValue",
        modifyStep.step.customStepValue.value
          ? modifyStep.step.customStepValue.value
          : modifyStep.step.customStepValue,
      );
    }
    formData.append(
      "createdBy",
      createdBy
        ? createdBy._id
        : modifyStep.step.createdBy
          ? modifyStep.step.createdBy._id
          : null,
    );
    formData.append(
      "timeTracking",
      JSON.stringify(modifyStep.step.timeTracking),
    );
    if (modifyStep.step.files && modifyStep.step.files.length !== 0)
      modifyStep.step.files.forEach((file: any) => {
        formData.append("files", file, file.name);
      });
    else formData.append("files", []);
    const images = modifyStep.step.images.filter(
      (img: any) => typeof img === "string",
    );
    formData.append("images", JSON.stringify(images));
    postData(
      `${urlEnum.orders}/saveStep/${orderContext.order._id}`,
      formData,
    ).then((res) => {
      if (res) {
        if (
          res.data.status === HttpStatusCode.OK ||
          res.data.status === HttpStatusCode.Created
        )
          orderContext.setOrder({
            ...orderContext.order,
            workflowSteps: res.data.result,
          });
      }
    });
  }

  return (
    <>
      <div
        className={
          phoneStyle ? styles.mainContainerMobile : styles.mainContainer
        }
      >
        <Filters
          filters={filters}
          recalculateReportsButton={false}
          orderStatus={{
            show: true,
            multiple: false,
            disableClearable: false,
          }}
          user={{
            show: true,
            multiple: true,
            disableClearable: false,
          }}
          customer={{
            show: true,
            multiple: true,
            disableClearable: false,
          }}
          device={{
            show: true,
            multiple: true,
            disableClearable: false,
          }}
          workflow={{
            show: true,
            multiple: false,
            disableClearable: false,
          }}
          workflowStatus={{
            show: true,
            multiple: false,
            disableClearable: false,
          }}
          product={{
            show: true,
            multiple: false,
            disableClearable: false,
          }}
        />
        <MainTable
          urlEnumApi={`${urlEnum.orders}/${ordersType.orders}`}
          header={phoneStyle ? ordersHeaderMobile : ordersHeader}
          tableTitle={Vocabulary.ordersList}
          deleteMessages={{
            title: Vocabulary.deleteOrder,
            text: Vocabulary.deleteConfirmationOrder,
          }}
          handleEdit={handleEdit}
          tableCustomOptions={{
            enableSort: true,
          }}
          hideOptionButtons={{ add: true }}
          customStyle={{
            hideCrtNumber: "excluded",
            tableStyle: styleForTableWithTabs,
            colorForRaw: (item: any) => {
              return priorityColors[item.priority] || "white";
            },
          }}
          headerButtons={
            <>
              <Button
                style={{
                  marginLeft: 8,
                  paddingLeft: 10,
                  paddingRight: 10,
                  verticalAlign: "middle",
                  backgroundColor: "#FFF",
                  color: "#000",
                  borderRadius: 50,
                }}
                onClick={async () => {
                  const newOrder = new OrderModel();
                  const res = await postData(urlEnum.orders, newOrder);
                  if (res && res.data && res.data.result) {
                    orderContext.setOrder(res.data.result);
                  }
                  setOpenCheckIn(true);
                }}
              >
                {Vocabulary.orderTicket}
              </Button>
            </>
          }
        />
      </div>
      <GenericModal
        open={openTimeTracking}
        onClose={() => {
          setOpenTimeTracking(false);
          orderContext.setOrder(new OrderModel());
        }}
        title={Vocabulary.timeTracking}
        styleTitle={{
          backgroundColor: theme.palette.primary?.main,
          color: "white",
          minWidth: 500,
        }}
      >
        <OrderWorkflow
          steps={orderContext.order.workflowSteps.filter((step) =>
            step.stepId?.name?.includes("Time"),
          )}
          handleOpenOrderStep={() => setOpenEditStep(true)}
          handleChangeModifyStep={(step, index) =>
            handleChangeModifyStep(step, index)
          }
        />
      </GenericModal>
      {openEditStep ? (
        <TimeTracking
          open={openEditStep}
          modifyStep={modifyStep}
          onClose={() => {
            setOpenEditStep(false);
            handleChangeModifyStep(null, -1);
          }}
          addOrUpdateStepToOrder={addOrUpdateStepToOrder}
          handleChangeModifyStep={(step: Step | null, index: number) =>
            handleChangeModifyStep(step, index)
          }
        />
      ) : null}
      {openCheckIn ? (
        <CheckInModal
          open={openCheckIn}
          onClose={async () => {
            setOpenCheckIn(false);
            await deleteData(
              `${urlEnum.orders}/trashedOrders/${orderContext.order._id}`,
            );
            orderContext.setOrder(new OrderModel());
          }}
        />
      ) : null}
    </>
  );
}
